<template>
  <div class="evaluation-page animate-fade-in-up">
    <h1 class="visually-hidden">离职风险评估</h1>
    <el-page-header
      @back="$router.push('/select')"
      content="离职风险评估"
      class="page-back-header"
    />

    <!-- 员工信息卡 -->
    <div class="employee-header-card">
      <div class="employee-avatar-large">
        {{ employee.real_name ? employee.real_name[0] : '?' }}
      </div>
      <div class="employee-details">
        <div class="employee-name-row">
          <h2 class="employee-name">{{ employee.real_name }}</h2>
          <el-button
            v-if="employeeEvalHistory.length > 0"
            size="small"
            icon="el-icon-s-data"
            class="btn-secondary"
            @click="showTrend = true"
          >
            稳定性轨迹
          </el-button>
        </div>
        <div class="employee-meta-text">
          {{ employee.dept_name || '-' }} · {{ employee.job_title || '-' }}
        </div>
      </div>
    </div>

    <div class="evaluation-body">
      <!-- 左侧行为评估区 -->
      <div class="evaluation-left">
        <!-- 评估说明区（培哥补充0502） -->
        <div v-if="activeInstruction" class="instruction-card">
          <div class="instruction-header" @click="instructionExpanded = !instructionExpanded">
            <i class="el-icon-document" style="color: var(--color-primary);"></i>
            <span class="instruction-title">评估说明</span>
            <i :class="instructionExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" class="instruction-toggle"></i>
          </div>
          <div v-show="instructionExpanded" class="instruction-body">
            <div class="instruction-content" v-html="activeInstruction.content"></div>
          </div>
        </div>

        <h3 class="section-title">
          <i class="el-icon-edit-outline" style="color: var(--color-primary);"></i>
          行为指标评估
        </h3>

        <!-- 按分类分组显示行为项 -->
        <div
          v-for="group in groupedBehaviors"
          :key="group.category_id"
          class="behavior-group"
        >
          <div class="behavior-group-title">
            <span class="group-name">{{ group.category_name }}</span>
          </div>

          <div class="behavior-group-card">
            <div
              v-for="(behavior, idx) in group.items"
              :key="behavior.behavior_id"
              class="behavior-item"
              :class="{ 'behavior-answered': answers[behavior.behavior_id] !== undefined, 'behavior-last': idx === group.items.length - 1 }"
            >
              <div class="behavior-header">
                <span class="behavior-index">{{ group.globalIndex + idx + 1 }}.</span>
                <div class="behavior-desc">{{ behavior.description }}</div>
                <div class="behavior-meta-inline">
                  <el-tooltip v-if="behavior.auto_fetch_enabled === 1" content="支持自动获取客观数据" placement="top">
                    <span class="meta-tag meta-tag-fetch" @click.stop="fetchObjectiveData(behavior)">
                      <i class="el-icon-refresh"></i>
                    </span>
                  </el-tooltip>
                  <el-tooltip v-if="behavior.evidence_desc" :content="behavior.evidence_desc" placement="top">
                    <span class="meta-tag meta-tag-data">
                      <i class="el-icon-time"></i>
                    </span>
                  </el-tooltip>
                  <el-tooltip v-if="behavior.hint_info" :content="behavior.hint_info" placement="top">
                    <span class="meta-tag meta-tag-hint">
                      <i class="el-icon-question"></i>
                    </span>
                  </el-tooltip>
                </div>
              </div>

              <el-radio-group
                v-model="answers[behavior.behavior_id]"
                @change="handleAnswer(behavior)"
                class="behavior-options"
              >
                <el-radio :label="1" class="option-yes">
                  <span class="option-label">是</span>
                </el-radio>
                <el-radio :label="2" class="option-no">
                  <span class="option-label">否</span>
                </el-radio>
                <el-radio :label="3" class="option-unknown">
                  <span class="option-label">不了解</span>
                </el-radio>
              </el-radio-group>
              <!-- 客观数据展示 -->
              <div v-if="objectiveData[behavior.behavior_id] && !objectiveData[behavior.behavior_id].loading" class="objective-data-box">
                <div v-if="objectiveData[behavior.behavior_id].has_data" class="objective-data-content">
                  <i class="el-icon-s-data" style="color: var(--color-primary);"></i>
                  <span>{{ objectiveData[behavior.behavior_id].data_summary }}</span>
                  <span v-if="objectiveData[behavior.behavior_id].last_updated" class="objective-data-time">更新于 {{ objectiveData[behavior.behavior_id].last_updated }}</span>
                </div>
                <div v-else class="objective-data-empty">
                  <i class="el-icon-info" style="color: var(--color-text-muted);"></i>
                  <span>暂无系统数据</span>
                </div>
              </div>
              <div v-else-if="objectiveData[behavior.behavior_id] && objectiveData[behavior.behavior_id].loading" class="objective-data-loading">
                <i class="el-icon-loading" style="color: var(--color-primary);"></i>
                <span>正在获取客观数据...</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧分数面板 -->
      <div class="evaluation-right">
        <div class="score-panel">
          <div class="score-label">当前评估分数</div>

          <div
            class="score-value"
            :class="'score-level-' + (totalScore > thresholds.mediumMax ? 'high' : (totalScore > thresholds.lowMax ? 'medium' : 'low'))"
          >
            <AnimatedNumber :value="totalScore" />
          </div>

          <div
            class="risk-badge"
            :class="'risk-badge-' + (totalScore > thresholds.mediumMax ? 'high' : (totalScore > thresholds.lowMax ? 'medium' : 'low'))"
          >
            {{ riskLevelText }}
          </div>

          <div class="progress-section">
            <div class="progress-label">
              评估进度
              <span class="progress-count">{{ answeredCount }} / {{ behaviors.length }}</span>
            </div>
            <el-progress
              :percentage="behaviors.length ? Math.round((answeredCount / behaviors.length) * 100) : 0"
              :color="progressColor"
              :stroke-width="8"
              :show-text="false"
            />
          </div>

          <!-- 评分规则说明区（培哥补充0502） -->
          <div class="score-rules">
            <div class="rule-title">
              <i class="el-icon-document"></i>
              评分规则说明
            </div>
            <div class="rule-content" v-html="scoringRulesContent"></div>
          </div>

          <el-button
            type="primary"
            size="large"
            class="submit-btn btn-click"
            :loading="submitting"
            :disabled="answeredCount < behaviors.length || $store.getters.isCurrentTaskExpired"
            @click="submitEvaluation"
          >
            <i class="el-icon-document-checked"></i>
            提交评估，生成报告
          </el-button>

          <div v-if="$store.getters.isCurrentTaskExpired" class="submit-hint">
            <i class="el-icon-warning"></i>
            当前任务已结束，无法提交评估
          </div>
          <div v-else-if="answeredCount < behaviors.length" class="submit-hint">
            <i class="el-icon-info"></i>
            请完成所有 {{ behaviors.length }} 项评估后提交
          </div>
        </div>
      </div>
    </div>

    <!-- 稳定性轨迹图 -->
    <el-dialog
      :title="'稳定性/风险指数趋势图 - ' + employee.real_name"
      :visible.sync="showTrend"
      width="800px"
      class="trend-dialog"
      :append-to-body="true"
      :modal-append-to-body="true"
    >
      <div ref="trendChart" class="trend-chart"></div>
      <div class="trend-interpretation">
        {{ trendInterpretation }}
      </div>
    </el-dialog>
  </div>
</template>

<script>
import AnimatedNumber from '../components/AnimatedNumber.vue'

export default {
  name: 'Evaluation',
  components: { AnimatedNumber },
  data() {
    return {
      answers: {},
      totalScore: 0,
      showTrend: false,
      instructionExpanded: true,
      trendChartInstance: null,
      objectiveData: {}, // behavior_id -> { has_data, data_summary }
      submitting: false
    }
  },
  computed: {
    thresholds() {
      return this.$store.state.settings.settings.thresholds || { lowMax: 4, mediumMax: 10 }
    },
    employee() {
      const id = parseInt(this.$route.params.id)
      const emp = this.$store.getters.employeeById(id)
      return emp || {}
    },
    behaviors() {
      return (this.$store.state.behaviors.behaviors || []).filter(b => b.status === 1)
    },
    categories() {
      return this.$store.state.behaviors.categories || []
    },
    // 按分类分组的行为项，用于前台分组展示
    groupedBehaviors() {
      const groups = []
      let globalIndex = 0
      for (const cat of this.categories) {
        const items = this.behaviors.filter(b => b.category_id === cat.category_id)
        if (items.length) {
          groups.push({
            category_id: cat.category_id,
            category_name: cat.category_name,
            items,
            globalIndex
          })
          globalIndex += items.length
        }
      }
      // 未分类的行为项归到最后
      const uncategorized = this.behaviors.filter(b => !this.categories.some(c => c.category_id === b.category_id))
      if (uncategorized.length) {
        groups.push({
          category_id: -1,
          category_name: '其他',
          items: uncategorized,
          globalIndex
        })
      }
      return groups
    },
    answeredCount() {
      return Object.values(this.answers).filter(v => v !== undefined && v !== null).length
    },
    riskLevelText() {
      if (this.totalScore > this.thresholds.mediumMax) return '高风险'
      if (this.totalScore > this.thresholds.lowMax) return '中风险'
      return '低风险'
    },
    progressColor() {
      if (this.totalScore > this.thresholds.mediumMax) return 'var(--color-high)'
      if (this.totalScore > this.thresholds.lowMax) return 'var(--color-medium)'
      return 'var(--color-low)'
    },
    // 培哥补充0502：评估说明
    activeInstruction() {
      return this.$store.getters.activeInstruction
    },
    // 培哥补充0502：评分规则说明
    scoringRulesContent() {
      const rules = this.$store.state.scoringRules.rules
      if (rules && rules.content) {
        return rules.content
      }
      return '加分项表明员工存在离职倾向，勾选"是"即累加对应分值。\n\n0-' + this.thresholds.lowMax + '分：低风险\n' + (this.thresholds.lowMax + 1) + '-' + this.thresholds.mediumMax + '分：中风险\n≥' + (this.thresholds.mediumMax + 1) + '分：高风险'
    },
    // 该员工的历史评估记录（按时间排序）
    employeeEvalHistory() {
      const id = this.employee.employee_id
      if (!id) return []
      const list = (this.$store.state.evaluations || [])
        .filter(e => e.employee_id === id)
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      return list
    },
    trendInterpretation() {
      const hist = this.employeeEvalHistory
      if (hist.length === 0) {
        return '暂无该员工的历史评估记录。完成本次评估后，系统将开始追踪其风险指数变化趋势。'
      }
      const first = hist[0]
      const last = hist[hist.length - 1]
      const change = last.total_score - first.total_score
      const direction = change > 0 ? '上升' : (change < 0 ? '下降' : '持平')
      return `该员工共完成 ${hist.length} 次评估，风险指数从 ${first.total_score} 分 ${direction} 至 ${last.total_score} 分。建议结合近期管理动作观察变化原因。`
    }
  },
  watch: {
    showTrend(val) {
      if (val) {
        this.$nextTick(() => this.renderTrendChart())
      } else if (this.trendChartInstance) {
        this.trendChartInstance.dispose()
        this.trendChartInstance = null
      }
    }
  },
  beforeDestroy() {
    if (this.trendChartInstance) {
      this.trendChartInstance.dispose()
      this.trendChartInstance = null
    }
  },
  created() {
    this.loadPreviousAnswers()
  },
  methods: {
    categoryNameOf(categoryId) {
      const cat = this.categories.find(c => c.category_id === categoryId)
      return cat ? cat.category_name : '-'
    },
    async fetchObjectiveData(behavior) {
      if (!behavior || behavior.auto_fetch_enabled !== 1) return
      this.$set(this.objectiveData, behavior.behavior_id, { loading: true })
      try {
        const res = await this.$api.fetchObjectiveData(behavior.behavior_id)
        if (res.data) {
          this.$set(this.objectiveData, behavior.behavior_id, {
            loading: false,
            has_data: res.data.has_data,
            data_summary: res.data.data_summary,
            last_updated: res.data.last_updated
          })
          this.$message.success('客观数据已获取')
        }
      } catch (e) {
        this.$set(this.objectiveData, behavior.behavior_id, { loading: false, error: true })
        this.$message.error('获取客观数据失败')
      }
    },
    loadPreviousAnswers() {
      const baseOnId = this.$route.query.base_on
      const history = this.employeeEvalHistory
      let target = null

      if (baseOnId) {
        // 以指定历史记录为基础评估
        target = history.find(e => String(e.evaluation_id) === String(baseOnId))
      }
      if (!target && history.length > 0) {
        // 没有指定 base_on，则取该员工最新一次评估
        target = history[history.length - 1]
      }

      if (target && target.answers) {
        this.answers = { ...target.answers }
        this.calculateScore()
      }
    },
    handleAnswer(behavior) {
      this.calculateScore()
    },
    calculateScore() {
      let score = 0
      for (const behavior of this.behaviors) {
        const answer = this.answers[behavior.behavior_id]
        if (answer === 1) {
          score += behavior.score
        }
      }
      this.totalScore = score
    },
    async submitEvaluation() {
      if (this.answeredCount < this.behaviors.length) {
        this.$message.warning('请先完成所有行为项的评估')
        return
      }

      this.submitting = true
      this.$message.info('正在提交评估...')

      try {
        // 1. 创建评估记录（store 未加载时用路由参数兜底）
        const empId = this.employee.employee_id || parseInt(this.$route.params.id)
        const createRes = await this.$api.createEvaluation({ employee_id: empId })
        const evaluationId = createRes.data.evaluation_id

        // 2. 构建答案映射（前端：1=是,2=否,3=不了解 → 后端：1=是,0=否）
        const answers = {}
        const answerDetails = []
        for (const b of this.behaviors) {
          const ans = this.answers[b.behavior_id]
          const backendAnswer = ans === 1 ? 1 : 0
          answers[String(b.behavior_id)] = backendAnswer
          answerDetails.push({
            behavior_id: b.behavior_id,
            behavior_desc: b.description,
            category_name: this.categoryNameOf(b.category_id),
            answer: backendAnswer,
            score: ans === 1 ? b.score : 0
          })
        }

        // 3. 提交评估
        const submitRes = await this.$api.submitEvaluation(evaluationId, {
          answers,
          answer_details: answerDetails
        })

        // 4. 同步到本地 store
        const evalData = {
          evaluation_id: evaluationId,
          employee_id: this.employee.employee_id,
          employee_name: this.employee.real_name,
          employee_email: this.employee.email || '',
          employee_no: this.employee.employee_no || '',
          total_score: submitRes.data.total_score,
          risk_level: submitRes.data.risk_level,
          risk_analysis: submitRes.data.risk_analysis,
          answers: { ...this.answers },
          answer_details: answerDetails,
          matched_suggestions: submitRes.data.matched_suggestions,
          matched_cases: submitRes.data.matched_cases,
          recommended_courses: submitRes.data.recommended_courses,
          created_at: new Date().toISOString().replace('T', ' ').slice(0, 19)
        }

        this.$store.commit('ADD_EVALUATION', evalData)
        this.$store.commit('SET_CURRENT_EVALUATION', evalData)

        // 5. 更新员工最新分数
        this.$store.commit('UPDATE_EMPLOYEE', {
          ...this.employee,
          latest_score: submitRes.data.total_score,
          latest_risk_level: submitRes.data.risk_level
        })

        this.$message.info('评估提交成功，正在生成智能建议报告...')
        try {
          const reportRes = await this.$api.generateAiReport(evaluationId)
          this.$store.commit('SET_CURRENT_AI_REPORT', reportRes.data)
          if (reportRes.data && reportRes.data.source === 'ai') {
            this.$message.success('智能建议报告已生成')
          } else {
            this.$message.warning('智能生成暂不可用，已生成规则报告')
          }
        } catch (reportError) {
          console.warn('[联调] 智能建议报告生成失败，报告页将尝试重新生成:', reportError)
          this.$store.commit('SET_CURRENT_AI_REPORT', null)
          this.$message.warning('智能生成暂不可用，报告页将展示规则结果')
        }
        this.$router.push('/report/' + this.employee.employee_id + '?evaluation_id=' + evaluationId)
      } catch (error) {
        console.error('[联调] 提交评估失败:', error)
        this.$message.error('提交评估失败，请稍后重试')
        this.submitting = false
      }
    },
    renderTrendChart() {
      const echarts = require('echarts')
      if (this.trendChartInstance) {
        this.trendChartInstance.dispose()
      }
      this.trendChartInstance = echarts.init(this.$refs.trendChart)

      const hist = this.employeeEvalHistory
      const t = this.thresholds
      const lowMax = t.lowMax || 4
      const mediumMax = t.mediumMax || 10

      if (hist.length === 0) {
        // 没有历史数据：展示风险区域说明图
        const yMax = mediumMax + 5
        this.trendChartInstance.setOption({
          title: {
            text: '暂无历史评估数据',
            subtext: '完成本次评估后，系统将开始追踪该员工的离职风险变化趋势',
            left: 'center',
            top: 'middle',
            textStyle: { fontSize: 16, color: '#999' },
            subtextStyle: { fontSize: 13, color: '#bbb' }
          },
          grid: { left: 60, right: 30, top: 40, bottom: 40 },
          xAxis: { type: 'category', data: [''], show: false },
          yAxis: {
            type: 'value',
            min: 0,
            max: yMax,
            name: '风险指数',
            splitLine: { lineStyle: { type: 'dashed', color: '#eee' } }
          },
          series: []
        })
        return
      }

      // 有真实历史数据
      const dates = hist.map(e => e.created_at ? e.created_at.slice(0, 10) : '')
      const scores = hist.map(e => e.total_score || 0)

      // 风险区域背景色
      const yMax = Math.max(mediumMax + 5, ...scores) + 2
      const markAreas = {
        silent: true,
        itemStyle: { opacity: 0.12 },
        data: [
          [{ yAxis: 0 }, { yAxis: lowMax, itemStyle: { color: '#10B981' } }],
          [{ yAxis: lowMax }, { yAxis: mediumMax, itemStyle: { color: '#F59E0B' } }],
          [{ yAxis: mediumMax }, { yAxis: yMax, itemStyle: { color: '#EF4444' } }]
        ]
      }

      // 计算每个数据点的颜色和大小（根据风险等级）
      const pointMeta = scores.map(s => {
        if (s > mediumMax) return { color: '#EF4444', size: 12, labelColor: '#fff' }
        if (s > lowMax) return { color: '#F59E0B', size: 10, labelColor: '#fff' }
        return { color: '#10B981', size: 10, labelColor: '#fff' }
      })

      this.trendChartInstance.setOption({
        grid: { left: 60, right: 40, top: 60, bottom: 50 },
        title: {
          text: '历史评估风险趋势',
          subtext: '共 ' + hist.length + ' 次评估记录',
          left: 'center',
          textStyle: { fontSize: 15, color: '#333' },
          subtextStyle: { fontSize: 12, color: '#999' }
        },
        legend: {
          data: ['风险指数', '低风险线', '中风险线'],
          bottom: 0
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255,255,255,0.96)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          textStyle: { color: '#374151' },
          extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.1); border-radius: 8px; padding: 12px;',
          formatter: function(params) {
            const score = params[0].value
            let level = '低风险'
            let color = '#10B981'
            let bg = '#ecfdf5'
            if (score > mediumMax) { level = '高风险'; color = '#EF4444'; bg = '#fef2f2' }
            else if (score > lowMax) { level = '中风险'; color = '#F59E0B'; bg = '#fffbeb' }
            return '<div style="font-weight:700;margin-bottom:8px;font-size:14px;color:#111;">' + params[0].axisValue + '</div>' +
              '<div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">' +
              '<span style="font-size:13px;color:#6b7280;">风险指数</span>' +
              '<span style="font-size:18px;font-weight:800;color:' + color + ';">' + score + '</span>' +
              '<span style="font-size:12px;color:#9ca3af;">分</span></div>' +
              '<div style="display:inline-block;padding:2px 10px;border-radius:12px;font-size:12px;font-weight:600;color:' + color + ';background:' + bg + ';">' + level + '</div>'
          }
        },
        visualMap: {
          show: false,
          type: 'piecewise',
          pieces: [
            { lte: lowMax, color: '#10B981' },
            { gt: lowMax, lte: mediumMax, color: '#F59E0B' },
            { gt: mediumMax, color: '#EF4444' }
          ],
          outOfRange: { color: '#9CA3AF' }
        },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
          axisLabel: { rotate: hist.length > 5 ? 30 : 0, color: '#666', fontSize: 12 },
          axisLine: { lineStyle: { color: '#ddd' } }
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: yMax,
          name: '风险指数',
          nameTextStyle: { color: '#666', fontSize: 12 },
          splitLine: { lineStyle: { type: 'dashed', color: '#eee' } },
          axisLine: { lineStyle: { color: '#ddd' } }
        },
        series: [
          {
            name: '风险指数',
            data: scores.map((s, i) => ({
              value: s,
              itemStyle: { borderWidth: 2, borderColor: '#fff' },
              symbolSize: pointMeta[i].size
            })),
            type: 'line',
            smooth: 0.35,
            symbol: 'circle',
            lineStyle: { width: 3.5 },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(156, 163, 175, 0.12)' },
                  { offset: 1, color: 'rgba(156, 163, 175, 0.02)' }
                ]
              }
            },
            markArea: markAreas,
            markLine: {
              silent: true,
              symbol: 'none',
              lineStyle: { type: 'dashed', width: 1.5 },
              data: [
                { yAxis: lowMax, lineStyle: { color: '#10B981' }, label: { formatter: '低风险线 (' + lowMax + '分)', position: 'end', color: '#10B981', fontSize: 11 } },
                { yAxis: mediumMax, lineStyle: { color: '#F59E0B' }, label: { formatter: '中风险线 (' + mediumMax + '分)', position: 'end', color: '#F59E0B', fontSize: 11 } }
              ]
            },
            markPoint: {
              data: [
                { type: 'max', name: '最高', itemStyle: { color: '#EF4444' }, label: { formatter: '最高\n{c}', color: '#fff', fontSize: 11 } },
                { type: 'min', name: '最低', itemStyle: { color: '#10B981' }, label: { formatter: '最低\n{c}', color: '#fff', fontSize: 11 } }
              ]
            }
          }
        ]
      })
    }
  }
}
</script>

<style scoped>
.evaluation-page {
  max-width: 1200px;
  margin: 0 auto;
}

.page-back-header {
  margin-bottom: var(--space-xl);
}

/* 员工信息卡 */
.employee-header-card {
  display: flex;
  align-items: center;
  gap: var(--space-lg);
  padding: var(--space-xl);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  margin-bottom: var(--space-xl);
  box-shadow: var(--shadow-sm);
}

.employee-avatar-large {
  width: 64px;
  height: 64px;
  border-radius: var(--radius-full);
  background: var(--color-primary-gradient);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  flex-shrink: 0;
}

.employee-details {
  flex: 1;
}

.employee-name-row {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  margin-bottom: var(--space-xs);
}

.employee-name {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
}

.employee-meta-text {
  font-size: 14px;
  color: var(--color-text-secondary);
}

/* 评估主体 */
.evaluation-body {
  display: flex;
  gap: var(--space-xl);
}

.evaluation-left {
  flex: 1;
  min-width: 0;
}

.evaluation-right {
  width: 300px;
  flex-shrink: 0;
}

/* 评估说明卡片 */
.instruction-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  margin-bottom: var(--space-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.instruction-header {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-md) var(--space-lg);
  cursor: pointer;
  background: var(--color-bg);
}

.instruction-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  flex: 1;
}

.instruction-toggle {
  color: var(--color-text-muted);
  font-size: 14px;
}

.instruction-body {
  padding: var(--space-lg);
}

.instruction-content {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.8;
  white-space: pre-wrap;
  font-family: inherit;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 var(--space-lg) 0;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.section-count {
  margin-left: auto;
  font-size: 13px;
  color: var(--color-text-muted);
  font-weight: 500;
  background: var(--color-bg);
  padding: 2px 10px;
  border-radius: var(--radius-full);
}

/* 行为分组 */
.behavior-group {
  margin-bottom: var(--space-xl);
}

.behavior-group-title {
  display: flex;
  align-items: center;
  padding: var(--space-sm) var(--space-md);
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: var(--space-sm);
}

.behavior-group-title .group-name {
  color: var(--color-primary);
}

/* 分类大卡片 */
.behavior-group-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

/* 行为项 */
.behavior-item {
  padding: var(--space-lg);
  border-bottom: 1px solid var(--color-border-light);
  transition: all var(--duration-normal) var(--ease-out);
}

.behavior-item:last-child,
.behavior-item.behavior-last {
  border-bottom: none;
}

.behavior-item:hover {
  background: var(--color-bg);
}

.behavior-answered {
  background: rgba(16, 185, 129, 0.04);
}

.behavior-header {
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
}

.behavior-index {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-muted);
  flex-shrink: 0;
  padding-top: 2px;
  min-width: 24px;
}

.behavior-desc {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
  line-height: 1.5;
}

.behavior-meta-inline {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  justify-content: flex-end;
}

.meta-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  border-radius: var(--radius-sm);
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
}

.meta-tag-fetch {
  background: rgba(59, 130, 246, 0.08);
  color: var(--color-primary);
}

.meta-tag-fetch:hover {
  background: var(--color-primary);
  color: white;
}

.meta-tag-data {
  background: rgba(16, 185, 129, 0.08);
  color: var(--color-low);
}

.meta-tag-data:hover {
  background: var(--color-low);
  color: white;
}

.meta-tag-hint {
  background: rgba(245, 158, 11, 0.08);
  color: var(--color-medium);
}

.meta-tag-hint:hover {
  background: var(--color-medium);
  color: white;
}

.behavior-options {
  padding-left: 32px;
  padding-top: var(--space-sm);
}

.behavior-options >>> .el-radio {
  margin-right: var(--space-xl);
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-md);
  transition: all var(--duration-fast) var(--ease-out);
}

.behavior-options >>> .el-radio:hover {
  background: var(--color-bg);
}

.option-label {
  font-weight: 600;
  margin-right: 4px;
}

.option-hint {
  color: var(--color-text-muted);
  font-size: 13px;
}

.option-question {
  color: var(--color-text-muted);
  margin-left: 4px;
  cursor: pointer;
  font-size: 14px;
}

.option-question:hover {
  color: var(--color-primary);
}

/* 分数面板 */
.score-panel {
  position: sticky;
  top: var(--space-xl);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-2xl);
  text-align: center;
  box-shadow: var(--shadow-sm);
}

.score-label {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-sm);
}

.score-value {
  font-size: 64px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: var(--space-md);
  transition: color var(--duration-normal) var(--ease-out);
}

.score-level-low { color: var(--color-low); }
.score-level-medium { color: var(--color-medium); }
.score-level-high { color: var(--color-high); }

.risk-badge {
  display: inline-block;
  padding: 6px 20px;
  border-radius: var(--radius-full);
  font-size: 16px;
  font-weight: 700;
  margin-bottom: var(--space-xl);
}

.risk-badge-low { background: var(--color-low-bg); color: var(--color-low-text); }
.risk-badge-medium { background: var(--color-medium-bg); color: var(--color-medium-text); }
.risk-badge-high { background: var(--color-high-bg); color: var(--color-high-text); }

/* 进度条 */
.progress-section {
  margin-bottom: var(--space-xl);
  text-align: left;
}

.progress-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-sm);
}

.progress-count {
  font-weight: 600;
  color: var(--color-text);
}

/* 评分规则 */
.score-rules {
  background: var(--color-bg);
  border-radius: var(--radius-md);
  padding: var(--space-lg);
  margin-bottom: var(--space-xl);
  text-align: left;
}

.rule-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: var(--space-md);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.rule-content {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.8;
  white-space: pre-wrap;
  font-family: inherit;
}

/* 提交按钮 */
.submit-btn {
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
}

.submit-hint {
  margin-top: var(--space-md);
  font-size: 12px;
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-xs);
}

/* 趋势图 */
.trend-chart {
  width: 100%;
  height: 400px;
}

.trend-interpretation {
  margin-top: var(--space-lg);
  padding: var(--space-lg);
  background: var(--color-bg);
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  font-size: 14px;
  line-height: 1.6;
}

/* 客观数据展示 */
.objective-data-box {
  margin-left: 32px;
  margin-top: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  background: rgba(59, 130, 246, 0.06);
  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: var(--radius-md);
}

.objective-data-content {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: 13px;
  color: var(--color-text);
  flex-wrap: wrap;
}

.objective-data-time {
  margin-left: auto;
  font-size: 12px;
  color: var(--color-text-muted);
}

.objective-data-empty {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: 13px;
  color: var(--color-text-muted);
}

.objective-data-loading {
  margin-left: 32px;
  margin-top: var(--space-sm);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: 13px;
  color: var(--color-text-secondary);
}
</style>
